/**
 * LandingPage — public entry point for MediaLabs.
 *
 * New structure (2026-04-28):
 *   - Two top-level nav items (Studio, Pricing) with the Studio MediaLabs
 *     logo sitting in the center of the menu.
 *   - Clicking Studio or Pricing opens a glassy, luminous overlay that sits
 *     below the nav and above the giant "MediaLabs" title. Clicking the same
 *     option again (or the X) closes it.
 *   - All CTAs route to auth or /studio depending on login state.
 */
import { useCallback, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { PrismaHero } from '../components/ui/prisma-hero';
import { LandingOverlayRoot } from '../components/ui/landing-overlays';
import '../styles/landing.css';

// Hero background loop — looping video with the MediaLabs logo intro
// Transcoded to 1080p H.264 main + faststart + 96kbps AAC (23.5 MB instead of 122 MB)
const HERO_VIDEO =
  'https://pub-063ce05a26c2404fa8909ca12197a910.r2.dev/medialabs_ini/video_con_logo_inicio_web.mp4';

const LOGO_SRC = '/img/logo_circular_medialabs.png';
// Multiple sources so every browser plays it. WebM/VP9 is universal in
// Chromium-based browsers; H.264 MP4 covers Safari + everything else.
const LOGO_VIDEO_SOURCES = [
  { src: '/img/logo_medialabs_giratorio.webm', type: 'video/webm' },
  { src: '/img/logo_medialabs_giratorio_web.mp4', type: 'video/mp4' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeOverlay, setActiveOverlay] = useState(null); // 'studio' | 'pricing' | null

  // Gracefully handle the case where Clerk isn't configured
  let isSignedIn = false;
  try {
    const auth = useAuth();
    isSignedIn = !!auth.isSignedIn;
  } catch {
    isSignedIn = false;
  }

  const openOverlay = useCallback((which) => (e) => {
    e?.preventDefault?.();
    setActiveOverlay((prev) => (prev === which ? null : which));
  }, []);

  const closeOverlay = useCallback(() => setActiveOverlay(null), []);

  const goToAuth = useCallback(() => {
    closeOverlay();
    if (isSignedIn) navigate('/studio');
    else navigate('/sign-up');
  }, [closeOverlay, isSignedIn, navigate]);

  const handlePricingCTA = useCallback((planId) => {
    closeOverlay();
    if (planId === 'free') {
      navigate(isSignedIn ? '/studio' : '/sign-up');
    } else if (planId === 'payg') {
      navigate(isSignedIn ? '/studio' : '/sign-up');
    } else {
      navigate('/sign-up');
    }
  }, [closeOverlay, isSignedIn, navigate]);

  const navItems = [
    {
      label: 'Studio',
      href: '#studio',
      onClick: openOverlay('studio'),
      active: activeOverlay === 'studio',
    },
    {
      label: 'Pricing',
      href: '#pricing',
      onClick: openOverlay('pricing'),
      active: activeOverlay === 'pricing',
    },
  ];

  const primaryCta = isSignedIn
    ? { label: 'Entrar al Studio', onClick: () => navigate('/studio') }
    : { label: 'Crear cuenta gratis', onClick: () => navigate('/sign-up') };

  const secondaryCta = isSignedIn
    ? null
    : { label: 'Iniciar sesión', onClick: () => navigate('/sign-in') };

  return (
    <div className="landing-root" data-testid="landing-page">
      <PrismaHero
        title="MediaLabs"
        description="Studio de generación multimedia — un laboratorio donde expandir los límites de tu creatividad. Conectá nodos de IA, generá imágenes, videos y música en un solo canvas."
        navItems={navItems}
        logo={{
          src: LOGO_SRC,
          videoSrc: LOGO_VIDEO_SOURCES,
          alt: 'Studio MediaLabs',
          onClick: closeOverlay,
        }}
        primaryCta={primaryCta}
        secondaryCta={secondaryCta}
        videoSrc={HERO_VIDEO}
        overlay={
          <LandingOverlayRoot
            active={activeOverlay}
            onClose={closeOverlay}
            onStudioCTA={goToAuth}
            onPricingCTA={handlePricingCTA}
          />
        }
      />
    </div>
  );
};

export default LandingPage;
